import { useEffect, useRef } from "react"
import { useTheme } from "@/clientWeb/contexts/ThemeContext"

interface RibbonBackgroundProps {
  children: React.ReactNode
  className?: string
}

export function RibbonBackground({ children, className = "" }: RibbonBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const { theme } = useTheme()

  useEffect(() => {
    const panel = panelRef.current
    const canvas = canvasRef.current
    if (!panel || !canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let W = 0, H = 0
    const dpr = window.devicePixelRatio || 1
    let t = 0
    const mouse = { x: 0, y: 0 }
    const smoothMouse = { x: 0, y: 0 }
    let mouseIn = false
    let animationId: number

    const isDark = () => theme === 'dark'

    const resize = () => {
      const rect = panel.getBoundingClientRect()
      W = rect.width
      H = rect.height
      canvas.width = Math.round(W * dpr)
      canvas.height = Math.round(H * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const handleMouseMove = (e: MouseEvent) => {
      const r = panel.getBoundingClientRect()
      mouse.x = e.clientX - r.left
      mouse.y = e.clientY - r.top
      mouseIn = true
    }

    const handleMouseLeave = () => {
      mouseIn = false
    }

    const hexToRgb = (hex: string) => ({
      r: parseInt(hex.slice(1, 3), 16),
      g: parseInt(hex.slice(3, 5), 16),
      b: parseInt(hex.slice(5, 7), 16),
    })

    const lerpRgb = (h1: string, h2: string, f: number) => {
      const a = hexToRgb(h1)
      const b = hexToRgb(h2)
      return {
        r: Math.round(a.r + (b.r - a.r) * f),
        g: Math.round(a.g + (b.g - a.g) * f),
        b: Math.round(a.b + (b.b - a.b) * f),
      }
    }

    const drawRibbon = ({
      lines,
      sx,
      sy,
      ex,
      ey,
      spread,
      curveFactor,
      c1,
      c2,
      opacity,
      phase,
    }: {
      lines: number
      sx: number
      sy: number
      ex: number
      ey: number
      spread: number
      curveFactor: number
      c1: string
      c2: string
      opacity: number
      phase: number
    }) => {
      const steps = 60 // Slightly lower steps for performance with more lines

      for (let i = 0; i < lines; i++) {
        const lp = i / lines
        const offset = (lp - 0.5) * spread
        const fade = Math.sin(lp * Math.PI) 
        const col = lerpRgb(c1, c2, lp)
        
        // Lower alpha per line so the stack doesn't become a solid block
        const alpha = Math.min(opacity * fade * 0.7, 1.0)

        ctx.beginPath()
        ctx.strokeStyle = `rgba(${col.r},${col.g},${col.b},${alpha.toFixed(3)})`
        ctx.lineWidth = 0.4 // Ultra thin lines
        ctx.lineCap = 'round'

        for (let s = 0; s <= steps; s++) {
          const p = s / steps
          const w1 = Math.sin(p * 7.8 + t * 0.2 + phase) * W * curveFactor * 0.25
          const w2 = Math.cos(p * 5.6 + t * 0.12 + phase + i * 0.04) * H * 0.1

          const x = sx + (ex - sx) * p + w1 + offset * Math.cos(p * Math.PI)
          let y = sy + (ey - sy) * p + w2 + offset * Math.sin(p * Math.PI * 1.5)

          if (mouseIn) {
            const dx = x - smoothMouse.x
            const dy = y - smoothMouse.y
            const d2 = dx * dx + dy * dy
            if (d2 < 40000 && d2 > 0) {
              const dist = Math.sqrt(d2)
              const push = Math.pow(1 - dist / 200, 2) * 45
              y += (dy / dist) * push
            }
          }

          s === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
        }
        ctx.stroke()
      }

      // Connector lines - reduced frequency to keep it clean
      for (let c = 0; c < 8; c++) {
        const cp = c / 8
        const col = lerpRgb(c1, c2, cp)
        const alpha = Math.min(opacity * 0.15 * Math.sin(cp * Math.PI), 1.0)

        ctx.beginPath()
        ctx.strokeStyle = `rgba(${col.r},${col.g},${col.b},${alpha.toFixed(3)})`
        ctx.lineWidth = 0.3
        
        for (let i = 0; i < lines; i++) {
          const lp = i / lines
          const offset = (lp - 0.5) * spread
          const w1 = Math.sin(cp * 7.8 + t * 0.2 + phase) * W * curveFactor * 0.25
          const w2 = Math.cos(cp * 5.6 + t * 0.12 + phase + i * 0.04) * H * 0.1
          const x = sx + (ex - sx) * cp + w1 + offset * Math.cos(cp * Math.PI)
          const y = sy + (ey - sy) * cp + w2 + offset * Math.sin(cp * Math.PI * 1.5)
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
        }
        ctx.stroke()
      }
    }

    const getRibbons = () => {
      const dark = isDark()
      // Slightly higher opacity for thin lines to remain visible
      const base = dark ? 0.2 : 0.4 
      
      // HUGE line counts for a dense mesh look
      const lBase = 60 

      return [
        {
          lines: lBase,
          sx: -W * 0.2, sy: -H * 0.1, ex: W * 1.2, ey: H * 1.1,
          spread: 120, curveFactor: 0.45, c1: '#67E8F9', c2: '#C084FC',
          opacity: base, phase: 0,
        },
        {
          lines: 45,
          sx: W * 0.6, sy: -H * 0.15, ex: -W * 0.15, ey: H * 1.1,
          spread: 100, curveFactor: 0.4, c1: '#A5B4FC', c2: '#F9A8D4',
          opacity: base * 0.9, phase: 2.0,
        },
        {
          lines: 40,
          sx: -W * 0.1, sy: H * 0.5, ex: W * 1.2, ey: H * 0.3,
          spread: 90, curveFactor: 0.35, c1: '#5EEAD4', c2: '#93C5FD',
          opacity: base * 0.85, phase: 4.0,
        },
        {
          lines: 35,
          sx: -W * 0.2, sy: H * 1.15, ex: W * 1.1, ey: H * 0.1,
          spread: 80, curveFactor: 0.38, c1: '#7DD3FC', c2: '#C4B5FD',
          opacity: base * 0.8, phase: 5.0,
        },
      ]
    }

    const frame = () => {
      ctx.clearRect(0, 0, W, H)
      smoothMouse.x += (mouse.x - smoothMouse.x) * 0.04
      smoothMouse.y += (mouse.y - smoothMouse.y) * 0.04
      getRibbons().forEach(drawRibbon)
      t += 0.016 
      animationId = requestAnimationFrame(frame)
    }

    const resizeObserver = new ResizeObserver(() => {
      ctx.resetTransform()
      resize()
    })

    panel.addEventListener('mousemove', handleMouseMove)
    panel.addEventListener('mouseleave', handleMouseLeave)
    resizeObserver.observe(panel)
    resize()
    frame()

    return () => {
      panel.removeEventListener('mousemove', handleMouseMove)
      panel.removeEventListener('mouseleave', handleMouseLeave)
      resizeObserver.disconnect()
      cancelAnimationFrame(animationId)
    }
  }, [theme])

  return (
    <div ref={panelRef} className={`relative overflow-hidden h-full ${className}`}>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
      <div className="relative z-10 h-full">{children}</div>
    </div>
  )
}